import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Search, Pencil, Trash2, ChevronLeft, ChevronRight,
  Plus, Minus, SlidersHorizontal, X, FileDown, PackageSearch,
} from 'lucide-react';
import { formatRp, formatDate } from '@/lib/format';
import { CashierBadge } from '@/components/CashierField';
import { useAuth } from '@/context/AuthContext';

export default function TransactionTable({
  pid,
  onEdit,
  onChanged,
  onAddIncome,
  onAddExpense,
  onMaterialConsumption,
  onExport,
}) {
  const { can } = useAuth();
  const canWrite = can('business.write');
  const canDelete = can('business.delete');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState('100');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [cashierFilter, setCashierFilter] = useState('all');
  const [sort, setSort] = useState('date_desc');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${pid}/transactions`, {
        params: {
          search: search || undefined,
          type: typeFilter,
          category: categoryFilter,
          cashier: cashierFilter,
          sort,
          page,
          limit: limit === 'all' ? 100000 : Number(limit),
        }
      });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (e) { toast.error('Gagal memuat transaksi'); }
    finally { setLoading(false); }
  }, [pid, search, typeFilter, categoryFilter, cashierFilter, sort, page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, categoryFilter, cashierFilter, sort, limit]);

  const pages =
    limit === 'all' ? 1 : Math.max(1, Math.ceil(total / Number(limit)));
  const activeFilters = [typeFilter !== 'all', categoryFilter !== 'all', cashierFilter !== 'all', sort !== 'date_desc'].filter(Boolean).length;

  const doDelete = async (t) => {
    try {
      await api.delete(`/transactions/${t.id}`);
      toast.success('Transaksi dihapus');
      onChanged?.();
      load();
    } catch (e) { toast.error('Gagal menghapus'); }
  };

  const resetFilters = () => { setTypeFilter('all'); setCategoryFilter('all'); setCashierFilter('all'); setSort('date_desc'); };

  return (
    <div className="card-fpccs p-4">
      {/* Header with title + icon buttons */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">Daftar Transaksi</div>
          <span className="text-[11px] text-subtle-fpccs">({total})</span>
        </div>
        <TooltipProvider delayDuration={200}>
          <div className="flex items-center gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onMaterialConsumption}
                  aria-label="Konsumsi Material"
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-[var(--fpccs-border)] bg-[var(--fpccs-surface-2)] text-[var(--fpccs-primary)] transition hover:border-[var(--fpccs-primary)]/50 hover:bg-[rgba(var(--fpccs-primary-rgb),0.10)]"
                >
                  <PackageSearch className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Konsumsi Material</TooltipContent>
            </Tooltip>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Export Laporan"
                  title="Export Laporan"
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-[var(--fpccs-border)] bg-[var(--fpccs-surface-2)] text-[var(--fpccs-primary)] transition hover:border-[var(--fpccs-primary)]/50 hover:bg-[rgba(var(--fpccs-primary-rgb),0.10)]"
                >
                  <FileDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onExport('excel')}>
                  Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExport('pdf')}>
                  PDF (.pdf)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {canWrite && <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="btn-icon-add-income"
                  onClick={onAddIncome}
                  className="flex h-8 items-center justify-center rounded-md border border-[rgba(47,191,113,0.35)] bg-[rgba(47,191,113,0.10)] px-2 text-[var(--fpccs-success)] transition hover:bg-[rgba(47,191,113,0.18)] sm:gap-1.5 sm:px-3"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden text-xs font-medium sm:inline">
                    Input Pemasukan
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="sm:hidden">Input Pemasukan</TooltipContent>
            </Tooltip>}

            {canWrite && <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="btn-icon-add-expense"
                  onClick={onAddExpense}
                  className="flex h-8 items-center justify-center rounded-md border border-[rgba(224,90,79,0.35)] bg-[rgba(224,90,79,0.10)] px-2 text-[var(--fpccs-danger)] transition hover:bg-[rgba(224,90,79,0.18)] sm:gap-1.5 sm:px-3"
                >
                  <Minus className="h-4 w-4" />
                  <span className="hidden text-xs font-medium sm:inline">
                    Input Pengeluaran
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="sm:hidden">Input Pengeluaran</TooltipContent>
            </Tooltip>}
          </div>
        </TooltipProvider>
      </div>

      {/* Search + Filter row - Desktop */}
      <div className="hidden sm:flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fpccs-text-subtle)]" />
          <Input
            data-testid="transaksi-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari material, supplier, catatan..."
            className="pl-9 bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="transaksi-type-filter" className="w-full sm:w-[140px] bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]"><SelectValue placeholder="Jenis" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Jenis</SelectItem>
            <SelectItem value="income">Pemasukan</SelectItem>
            <SelectItem value="expense">Pengeluaran</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger data-testid="transaksi-category-filter" className="w-full sm:w-[160px] bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]"><SelectValue placeholder="Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            <SelectItem value="Material">Material</SelectItem>
            <SelectItem value="Mobilitas">Mobilitas</SelectItem>
            <SelectItem value="Gaji Tukang">Gaji Tukang</SelectItem>
            <SelectItem value="Gaji Member">Gaji Member</SelectItem>
            <SelectItem value="Lainnya">Lainnya</SelectItem>
          </SelectContent>
        </Select>
        <Select value={cashierFilter} onValueChange={setCashierFilter}>
          <SelectTrigger data-testid="transaksi-cashier-filter" className="w-full sm:w-[140px] bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]"><SelectValue placeholder="Kasir" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kasir</SelectItem>
            <SelectItem value="Agi">Agi</SelectItem>
            <SelectItem value="Albi">Albi</SelectItem>
            <SelectItem value="Lainnya">Lainnya</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="transaksi-sort" className="w-full sm:w-[140px] bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]"><SelectValue placeholder="Urutkan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="date_desc">Terbaru</SelectItem>
            <SelectItem value="date_asc">Terlama</SelectItem>
            <SelectItem value="total_desc">Nominal ↓</SelectItem>
            <SelectItem value="total_asc">Nominal ↑</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Search + Filter icon - Mobile */}
      <div className="sm:hidden flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fpccs-text-subtle)]" />
          <Input
            data-testid="transaksi-search-input-mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari..."
            className="pl-9 bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]"
          />
        </div>
        <button
          data-testid="btn-mobile-filter"
          onClick={() => setMobileFilterOpen(true)}
          className="relative shrink-0 w-10 h-10 rounded-md border border-[var(--fpccs-border)] bg-[var(--fpccs-surface-2)] flex items-center justify-center text-[var(--fpccs-text-muted)]"
        >
          <SlidersHorizontal className="w-4 h-4" />
          {activeFilters > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[var(--fpccs-primary)] text-[10px] flex items-center justify-center text-[var(--fpccs-bg)] font-semibold">{activeFilters}</span>
          )}
        </button>
      </div>

      {/* Mobile Filter Sheet */}
      <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
        <SheetContent side="right" className="bg-[var(--fpccs-surface-1)] border-l border-[var(--fpccs-border)] w-[85vw] max-w-sm">
          <SheetHeader>
            <SheetTitle>Filter Transaksi</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div>
              <div className="text-xs text-muted-fpccs mb-1.5">Jenis</div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="mobile-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Jenis</SelectItem>
                  <SelectItem value="income">Pemasukan</SelectItem>
                  <SelectItem value="expense">Pengeluaran</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs text-muted-fpccs mb-1.5">Kategori</div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="mobile-category-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Kategori</SelectItem>
                  <SelectItem value="Material">Material</SelectItem>
                  <SelectItem value="Mobilitas">Mobilitas</SelectItem>
                  <SelectItem value="Gaji Tukang">Gaji Tukang</SelectItem>
                  <SelectItem value="Gaji Member">Gaji Member</SelectItem>
                  <SelectItem value="Lainnya">Lainnya</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs text-muted-fpccs mb-1.5">Kasir</div>
              <Select value={cashierFilter} onValueChange={setCashierFilter}>
                <SelectTrigger data-testid="mobile-cashier-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Kasir</SelectItem>
                  <SelectItem value="Agi">Agi</SelectItem>
                  <SelectItem value="Albi">Albi</SelectItem>
                  <SelectItem value="Lainnya">Lainnya</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs text-muted-fpccs mb-1.5">Urutan</div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="mobile-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Tanggal Terbaru</SelectItem>
                  <SelectItem value="date_asc">Tanggal Terlama</SelectItem>
                  <SelectItem value="total_desc">Nominal Terbesar</SelectItem>
                  <SelectItem value="total_asc">Nominal Terkecil</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={resetFilters}><X className="w-3.5 h-3.5 mr-1" /> Reset</Button>
              <Button className="flex-1 bg-[var(--fpccs-primary)] text-[var(--fpccs-bg)]" onClick={() => setMobileFilterOpen(false)}>Terapkan</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <div
        data-testid="transaksi-table"
        className="max-h-[70vh] overflow-auto rounded-lg border border-[var(--fpccs-border)]"
      >
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[var(--fpccs-border)] hover:bg-transparent">
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs">
                Tanggal
              </TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs">Material</TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs text-right">Qty</TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs">Satuan</TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs text-right">Harga</TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs text-right">Total</TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs">Kategori</TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs">Supplier</TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs">Jenis</TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs">Kasir</TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs">Catatan</TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] text-[11px] uppercase tracking-wider text-subtle-fpccs">No. Invoice</TableHead>
              <TableHead className="sticky top-0 z-10 bg-[var(--fpccs-surface-1)] w-24 text-right text-[11px] uppercase tracking-wider text-subtle-fpccs">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i} className="border-b border-[var(--fpccs-border)]">
                  {[...Array(13)].map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} className="text-center text-muted-fpccs py-10">
                  Belum ada transaksi. Tambahkan pemasukan atau pengeluaran untuk mulai.
                </TableCell>
              </TableRow>
            ) : items.map((t) => (
              <TableRow key={t.id} data-testid={`tx-row-${t.id}`} className="border-b border-[var(--fpccs-border)] hover:bg-white/[0.03]">
                <TableCell className="text-xs whitespace-nowrap">{formatDate(t.date)}</TableCell>
                <TableCell className="text-xs">{t.material_name || '-'}</TableCell>
                <TableCell className="text-xs text-right font-mono">{t.qty ?? '-'}</TableCell>
                <TableCell className="text-xs text-muted-fpccs">{t.unit || '-'}</TableCell>
                <TableCell className="text-xs text-right font-mono">{t.unit_price ? formatRp(t.unit_price) : '-'}</TableCell>
                <TableCell className={`text-xs text-right font-mono font-semibold ${t.type === 'income' ? 'text-[var(--fpccs-success)]' : 'text-[var(--fpccs-danger)]'}`}>{formatRp(t.total)}</TableCell>
                <TableCell className="text-xs text-muted-fpccs">{t.category || (t.type === 'income' ? (t.termin || '-') : '-')}</TableCell>
                <TableCell className="text-xs">{t.supplier_name || '-'}</TableCell>
                <TableCell>
                  <span className={`text-[11px] px-1.5 py-0.5 rounded border ${t.type === 'income' ? 'bg-[rgba(47,191,113,0.14)] text-[#2FBF71] border-[rgba(47,191,113,0.28)]' : 'bg-[rgba(224,90,79,0.14)] text-[#E05A4F] border-[rgba(224,90,79,0.28)]'}`}>
                    {t.type === 'income' ? 'Pemasukan' : 'Pengeluaran'}
                  </span>
                </TableCell>
                <TableCell><CashierBadge cashier={t.cashier} cashier_other={t.cashier_other} /></TableCell>
                <TableCell className="text-xs text-muted-fpccs max-w-[220px] truncate">{t.note}</TableCell>
                <TableCell className="text-xs font-mono whitespace-nowrap text-[var(--fpccs-primary)]">{t.invoice_number || '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canWrite && <Button size="icon" variant="ghost" data-testid={`btn-edit-tx-${t.id}`} onClick={() => onEdit(t)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>}
                    {canDelete && <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" data-testid={`btn-delete-tx-${t.id}`} className="text-[var(--fpccs-danger)]">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="confirm-delete-transaction">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Hapus transaksi ini?</AlertDialogTitle>
                          <AlertDialogDescription>Transaksi akan dihapus permanen dan dashboard akan menghitung ulang.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Batal</AlertDialogCancel>
                          <AlertDialogAction data-testid={`btn-confirm-delete-tx-${t.id}`} onClick={() => doDelete(t)} className="bg-[var(--fpccs-danger)] text-[var(--fpccs-bg)]">Hapus</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 flex flex-col gap-3 border-t border-[var(--fpccs-border)] pt-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-xs text-subtle-fpccs">
          <span>
            Total:{' '}
            <span className="font-medium text-[var(--fpccs-text)]">
              {total}
            </span>{' '}
            transaksi
          </span>

          <Select value={limit} onValueChange={setLimit}>
            <SelectTrigger className="h-8 w-[145px] border-[var(--fpccs-border)] bg-[var(--fpccs-surface-2)] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="100">100 baris</SelectItem>
              <SelectItem value="all">Semua transaksi</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {limit !== 'all' && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              data-testid="btn-prev-page"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>

            <span className="text-xs text-muted-fpccs">
              Hal {page} / {pages}
            </span>

            <Button
              size="sm"
              variant="outline"
              data-testid="btn-next-page"
              disabled={page >= pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
